/**
 * A mowing bid the way the customer should read it (owner, Sep 19 2026):
 * the ONE-TIME price first, then what recurring costs — no contract — and how
 * the first-visit fees come back after the third mow. When the office agreed
 * a first-mow day that is not the route's day, the two dates they may choose
 * between sit underneath.
 *
 * Self-contained on purpose: it takes the server's numbers
 * (services/recurringCredit.offerFor, services/autoQuote.startOptionsFor) and
 * draws them, so the page around it can be rebuilt without touching the rule.
 */
import React from 'react';

export type MowingOfferData = {
  frequency: string | null;
  every: string | null;
  one_time: { total: number; made_of: { label: string; amount: number }[] };
  per_mow: number;
  /** Done every visit besides the mow, each with its own price (leaf raking $50): "$100 a mow, $50 a rake". */
  extras?: { label: string; amount: number }[];
  visit_total?: number;
  overgrowth: number;
  first_visit: number;
  credit: { amount: number; after_mows: number } | null;
  mows: { n: number; amount: number; credit?: number; onward?: boolean }[] | null;
  no_contract: boolean;
};
// Every date and every word about dates here is the server's (services/autoQuote.startPlansFor) — the same answer
// accept books. `then_words` is the whole "then every other Monday, from Monday, October 26"; nothing is worked out
// in the browser (owner, Oct 5 2026: "start days … based on new route days").
export type StartOption = { key: string; label?: string | null; first: string; then: string | null; one_off: boolean; then_words?: string | null };
export type StartOptionsData = {
  day: string | null;
  every: string | null;
  frequency: string;
  gap_days: number;
  default: string;
  options: StartOption[];
};

// A credit bigger than one mow makes that mow's line negative: "−$5", never "$-5".
const money = (n: number) => { const v = Math.round(Number(n) * 100) / 100; return `${v < 0 ? '−' : ''}$${Math.abs(v).toFixed(2).replace(/\.00$/, '')}`; };
const longDay = (iso: string) => {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};
/** "Then every other Monday, from Monday, October 26." — the server's words; older replies carry only the date. */
export const thenLine = (o: StartOption, every?: string | null) =>
  o.then_words ? `${o.then_words.charAt(0).toUpperCase()}${o.then_words.slice(1)}.`
    : o.then ? `Then ${every || 'on your route day'}, from ${longDay(o.then)}.` : '';

type Props = {
  offer: MowingOfferData;
  start?: StartOptionsData | null;
  /** Which first-mow date is picked; null = the default. */
  choice?: string | null;
  onChoice?: (key: string) => void;
  /** Already accepted or closed: show, do not ask. */
  locked?: boolean;
  /** A business account: "a visit", not "a mow"; no "pause any time". */
  commercial?: boolean;
};

export default function MowingOffer({ offer, start, choice, onChoice, locked, commercial = false }: Props) {
  const visitWord = commercial ? 'visit' : 'mow';
  const recurring = !!offer.every && offer.frequency !== 'one_time';
  // The one-time card earns its place only when a single visit costs MORE than a recurring one (a one-time visit fee,
  // travel). Work done every visit (leaf raking $50) is in both prices, so it is no reason to show a second card
  // (owner, Oct 27 2026: "why does it say one time mow" on a bid with no fee).
  const hasFees = offer.one_time.total > (offer.visit_total ?? offer.per_mow) + 0.004;
  const showOneTime = hasFees || !recurring;
  const picked = start ? (choice && start.options.some(o => o.key === choice) ? choice : start.default) : null;

  return (
    <section className="space-y-4" aria-label="Your price">
      {offer.overgrowth > 0 && (
        <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-3" role="note" aria-label="Overgrown lawn">
          <p className="text-[11px] uppercase tracking-widest text-amber-800">Your lawn is overgrown right now</p>
          <p className="mt-1 text-lg font-bold text-gray-900">
            First {visitWord}: {money(offer.first_visit)} <span className="text-sm font-medium text-amber-900">— includes a one-time {money(offer.overgrowth)} catch-up</span>
          </p>
          <p className="mt-0.5 text-sm text-amber-900">
            {recurring ? `Then ${money(offer.per_mow)} a ${visitWord}. ` : ''}The catch-up is charged once, on the first visit only, and never repeats unless the lawn is let go again.
          </p>
        </div>
      )}
      <div className={`grid gap-3 ${showOneTime && recurring ? 'sm:grid-cols-2' : ''}`}>
        {showOneTime && (
          <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-soft">
            <p className="text-[11px] uppercase tracking-widest text-gray-600">One-time {visitWord}</p>
            <p className="mt-1 text-3xl font-bold text-gray-900">{money(offer.one_time.total)}</p>
            {hasFees && (
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                {offer.one_time.made_of.map(m => (
                  <li key={m.label} className="flex justify-between gap-3"><span>{m.label}</span><span className="tabular-nums text-gray-900">{money(m.amount)}</span></li>
                ))}
              </ul>
            )}
            {offer.overgrowth > 0 && (
              <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                Your lawn is overgrown right now: this first visit also carries a one-time {money(offer.overgrowth)} catch-up, {money(offer.one_time.total + offer.overgrowth)} in all. It never repeats unless the lawn is let go again.
              </p>
            )}
          </div>
        )}

        {recurring && (
          <div className="rounded-3xl border-2 border-brand-600 bg-white p-4 shadow-brand">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[11px] uppercase tracking-widest text-brand-700">{offer.every}</p>
              {offer.no_contract && <span className="shrink-0 rounded-full bg-brand-50 text-brand-700 border border-brand-200 text-[11px] font-semibold px-2 py-0.5">No contract</span>}
            </div>
            <p className="mt-1 text-3xl font-bold text-gray-900">{money(offer.per_mow)} <span className="text-base font-medium text-gray-500">a {visitWord}</span></p>
            {(offer.extras || []).map(e => (
              <p key={e.label} className="mt-1 text-base font-semibold text-gray-800">+ {money(e.amount)} <span className="font-medium text-gray-500">a {visitWord} for {e.label.charAt(0).toLowerCase()}{e.label.slice(1)}</span></p>
            ))}
            {offer.mows && offer.credit ? (
              <>
                <ol className="mt-3 grid grid-cols-4 gap-1.5 text-center">
                  {offer.mows.map(m => (
                    <li key={m.n} className={`rounded-xl px-1 py-2 ${m.credit ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-100'}`}>
                      <p className="text-[10px] uppercase tracking-wide text-gray-500">{m.onward ? 'Then' : `Mow ${m.n}`}{m.amount < 0 ? ' · credit' : ''}</p>
                      <p className={`text-sm font-bold tabular-nums ${m.credit ? 'text-emerald-700' : 'text-gray-900'}`}>{money(m.amount)}</p>
                    </li>
                  ))}
                </ol>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                  Your first mow is billed at the one-time price. After your {offer.credit.after_mows === 3 ? 'third' : `${offer.credit.after_mows}th`} mow
                  we credit the {money(offer.credit.amount)} back, so you have paid {money(offer.per_mow)} a mow.{offer.credit.amount > offer.per_mow ? ' The credit is larger than one mow, so the rest comes off your bill.' : ''} Stop before then and the first visit simply stays at the one-time price.
                  {offer.overgrowth > 0 ? ` Mow 1 includes the ${money(offer.overgrowth)} overgrown catch-up, which is not credited.` : ''}
                </p>
              </>
            ) : (
              <p className="mt-2 text-sm text-gray-600">{commercial ? 'The per-visit price holds for twelve months from the first visit.' : 'Pause or stop any time.'}{!showOneTime && offer.overgrowth > 0 ? ` The first visit also carries a one-time ${money(offer.overgrowth)} overgrown catch-up, ${money(offer.first_visit)} that visit.` : ''}</p>
            )}
          </div>
        )}
      </div>

      {start && start.options.length > 0 && (
        <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-soft">
          {start.options.length > 1 && !locked ? (
            <>
              <p className="text-sm font-semibold text-gray-900">When should we start?</p>
              <p className="text-xs text-gray-500 mt-0.5">The price is the same either way{start.every ? `. After the first mow you are on the route ${start.every}` : ''}.</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={`First ${visitWord} date`}>
                {start.options.map(o => {
                  const on = picked === o.key;
                  return (
                    <button key={o.key} type="button" role="radio" aria-checked={on} onClick={() => onChoice && onChoice(o.key)}
                      className={`press text-left rounded-2xl border-2 px-3.5 py-3 transition ${on ? 'border-brand-600 bg-brand-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-[11px] uppercase tracking-widest text-gray-500">{o.label || (o.key === 'soon' ? 'Soonest' : 'Your route day')}</span>
                        <span className={`h-4 w-4 rounded-full border-2 ${on ? 'border-brand-600 bg-brand-600 ring-2 ring-inset ring-white' : 'border-gray-300'}`} aria-hidden />
                      </span>
                      <span className="block mt-1 text-base font-bold text-gray-900">{longDay(o.first)}</span>
                      <span className="block text-xs text-gray-600 mt-0.5">{thenLine(o, start.every)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (() => {
            const o = start.options.find(x => x.key === picked) || start.options[0];
            return <p className="text-sm text-gray-700"><span className="font-semibold text-gray-900">{start.frequency === 'one_time' ? `Your ${visitWord}` : `First ${visitWord}`}: {longDay(o.first)}.</span> {thenLine(o, start.every)}</p>;
          })()}
        </div>
      )}
    </section>
  );
}
